import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Loader2, Landmark, Building2, ShieldCheck, Layers, FileText, Users, Clock,
  ShieldAlert, TrendingUp, MapPin, Sparkles, Plus, ArrowRight, ExternalLink,
  CheckCircle2, AlertTriangle, Send,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/apiClient";
import { toast } from "sonner";

const STAGE_LABEL = {
  discovered: "Discovered", review: "In Review", go_no_go: "Go / No-Go", preparation: "Preparation",
  internal_approval: "Internal Approval", submitted: "Submitted", awarded: "Awarded", not_awarded: "Not Awarded",
};
const STAGE_TONE = {
  discovered: "bg-sky-500/15 text-sky-300", review: "bg-amber-500/15 text-amber-300",
  go_no_go: "bg-amber-500/15 text-amber-300", preparation: "bg-sky-500/15 text-sky-300",
  internal_approval: "bg-purple-500/15 text-purple-300", submitted: "bg-teal-500/15 text-teal-300",
  awarded: "bg-teal-500/20 text-teal-200", not_awarded: "bg-white/10 text-blue-200",
};
const SEC_TONE = {
  active: "bg-teal-100 text-teal-700", partial: "bg-amber-100 text-amber-700",
  not_certified: "bg-slate-100 text-slate-500",
};

const money = (n) => "$" + Number(n || 0).toLocaleString();

export default function Enterprise() {
  const { user, loading } = useAuth();
  const navigate = useNavigate();
  const isMgmt = user && ["admin", "executive", "manager"].includes(user.role);
  const isAdmin = user && user.role === "admin";

  useEffect(() => {
    if (loading) return;
    if (!user) { navigate("/login"); return; }
    if (!["admin", "executive", "manager", "staff"].includes(user.role)) { navigate("/dashboard"); return; }
  }, [user, loading, navigate]);

  if (loading || !user) return <div className="py-32 grid place-items-center bg-blue-950 min-h-[70vh]"><Loader2 className="w-8 h-8 animate-spin text-teal-400" /></div>;

  return (
    <div className="bg-blue-950 text-white min-h-screen" data-testid="enterprise-page">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="flex items-center gap-2 mb-1"><Landmark className="w-5 h-5 text-teal-400" /><p className="text-xs uppercase tracking-[0.18em] text-teal-400 font-semibold">T5 · Phase 5</p></div>
        <h1 className="font-display text-3xl lg:text-5xl font-bold">Enterprise &amp; Government</h1>
        <p className="text-blue-200/70 mt-2">Government opportunity pipeline, program management, enterprise command and an honest security posture. AI prepares — <span className="text-teal-300">humans authorize every submission.</span></p>

        <Tabs defaultValue={isMgmt ? "command" : "gov"} className="mt-8">
          <TabsList className="bg-white/5 flex-wrap h-auto" data-testid="ent-tabs">
            {isMgmt && <TabsTrigger value="command" data-testid="ent-tab-command" className="data-[state=active]:bg-teal-500">Command Center</TabsTrigger>}
            <TabsTrigger value="gov" data-testid="ent-tab-gov" className="data-[state=active]:bg-teal-500">Government Pipeline</TabsTrigger>
            <TabsTrigger value="profile" data-testid="ent-tab-profile" className="data-[state=active]:bg-teal-500">Capability Profile</TabsTrigger>
            <TabsTrigger value="programs" data-testid="ent-tab-programs" className="data-[state=active]:bg-teal-500">Programs</TabsTrigger>
            {isAdmin && <TabsTrigger value="security" data-testid="ent-tab-security" className="data-[state=active]:bg-teal-500">Security</TabsTrigger>}
          </TabsList>

          {isMgmt && <TabsContent value="command" className="mt-6"><CommandPanel /></TabsContent>}
          <TabsContent value="gov" className="mt-6"><GovPanel isMgmt={isMgmt} isAdmin={isAdmin} /></TabsContent>
          <TabsContent value="profile" className="mt-6"><ProfilePanel isAdmin={isAdmin} /></TabsContent>
          <TabsContent value="programs" className="mt-6"><ProgramsPanel isMgmt={isMgmt} /></TabsContent>
          {isAdmin && <TabsContent value="security" className="mt-6"><SecurityPanel /></TabsContent>}
        </Tabs>
      </div>
    </div>
  );
}

function CommandPanel() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/enterprise/command").then((r) => setD(r.data)).catch(() => toast.error("Could not load command center")); }, []);
  if (!d) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-teal-400" /></div>;
  const CARDS = [
    { icon: Building2, label: "Locations", value: d.locations, tone: "text-sky-300" },
    { icon: Users, label: "Your workforce", value: d.workforce_org, tone: "text-teal-300" },
    { icon: Users, label: "Network workforce", value: d.workforce_network, tone: "text-sky-300" },
    { icon: Clock, label: "Open shifts", value: d.open_shifts, tone: "text-amber-300" },
    { icon: ShieldAlert, label: "Credentials expiring ≤60d", value: d.credentials_expiring, tone: "text-amber-300" },
    { icon: ShieldAlert, label: "Pending approvals", value: d.pending_approvals, tone: "text-amber-300" },
    { icon: Landmark, label: "Government opportunities", value: d.gov_opportunities, tone: "text-sky-300" },
  ];
  return (
    <div data-testid="ent-command">
      <p className="text-blue-200/80 mb-4">{d.org_name} · operating in {d.states?.length ? d.states.join(", ") : "your service area"}</p>
      <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
        {CARDS.map((c) => (
          <div key={c.label} data-testid={`ent-cmd-${c.label.replace(/\s+/g, "-").toLowerCase()}`} className="bg-white/5 border border-white/10 rounded-2xl p-4">
            <c.icon className={`w-5 h-5 ${c.tone}`} /><p className="font-display text-3xl font-bold mt-2">{c.value ?? 0}</p><p className="text-xs text-blue-200/70 mt-0.5">{c.label}</p>
          </div>
        ))}
        <div className="bg-teal-500/15 border border-teal-400/30 rounded-2xl p-4"><TrendingUp className="w-5 h-5 text-teal-300" /><p className="font-display text-2xl font-bold mt-2" data-testid="ent-cmd-pipeline">{money(d.pipeline_value)}</p><p className="text-xs text-teal-100/80 mt-0.5">Open pipeline value</p></div>
      </div>
    </div>
  );
}

function GovPanel({ isMgmt, isAdmin }) {
  const [opps, setOpps] = useState([]);
  const [stages, setStages] = useState([]);
  const [loaded, setLoaded] = useState(false);
  const [busy, setBusy] = useState(null);
  const [expanded, setExpanded] = useState(null);
  const [adding, setAdding] = useState(false);
  const [form, setForm] = useState({ title: "", agency: "", jurisdiction: "", solicitation_no: "", deadline: "", source_url: "", description: "" });

  const load = () => api.get("/gov/opportunities").then((r) => { setOpps(r.data.opportunities); setStages(r.data.stages); }).catch(() => {}).finally(() => setLoaded(true));
  useEffect(() => { load(); }, []);

  const analyze = async (id) => {
    setBusy(id + "-a");
    try { const { data } = await api.post(`/gov/opportunities/${id}/analyze`); setOpps((o) => o.map((x) => x.id === id ? { ...x, analysis: data.analysis, checklist: data.checklist, stage: "review" } : x)); setExpanded(id); }
    catch (e) { toast.error(e?.response?.data?.detail || "Analysis failed"); } finally { setBusy(null); }
  };
  const setStage = async (id, stage) => {
    try { const { data } = await api.patch(`/gov/opportunities/${id}`, { stage }); setOpps((o) => o.map((x) => x.id === id ? data : x)); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not update stage"); }
  };
  const prepare = async (id) => {
    setBusy(id + "-p");
    try { const { data } = await api.post(`/gov/opportunities/${id}/prepare-submission`); toast.success("Routed to the Approval Center"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Could not prepare"); } finally { setBusy(null); }
  };
  const submit = async (id) => {
    setBusy(id + "-s");
    try { await api.post(`/gov/opportunities/${id}/submit`); toast.success("Submission recorded"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Submission blocked — internal approval required first"); } finally { setBusy(null); }
  };
  const addOpp = async (e) => {
    e.preventDefault();
    try { await api.post("/gov/opportunities", form); toast.success("Opportunity added"); setForm({ title: "", agency: "", jurisdiction: "", solicitation_no: "", deadline: "", source_url: "", description: "" }); setAdding(false); load(); }
    catch (er) { toast.error(er?.response?.data?.detail || "Could not add"); }
  };

  return (
    <div data-testid="ent-gov">
      <div className="flex items-center justify-between flex-wrap gap-2 mb-4">
        <p className="text-sm text-blue-200/70">Sourced public-sector opportunities. The Government Agent summarizes and builds a checklist — <span className="text-teal-300">it never auto-submits.</span></p>
        {isAdmin && <Button onClick={() => setAdding((a) => !a)} data-testid="gov-add-toggle" className="bg-teal-500 hover:bg-teal-600 gap-1.5"><Plus className="w-4 h-4" /> Add opportunity</Button>}
      </div>

      {adding && isAdmin && (
        <form onSubmit={addOpp} className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-5 grid sm:grid-cols-2 gap-3" data-testid="gov-add-form">
          <Input required placeholder="Title" value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} data-testid="gov-f-title" className="bg-white/10 border-white/15 text-white placeholder:text-blue-200/50" />
          <Input required placeholder="Agency" value={form.agency} onChange={(e) => setForm({ ...form, agency: e.target.value })} data-testid="gov-f-agency" className="bg-white/10 border-white/15 text-white placeholder:text-blue-200/50" />
          <Input required placeholder="Jurisdiction (e.g. State — Ohio)" value={form.jurisdiction} onChange={(e) => setForm({ ...form, jurisdiction: e.target.value })} data-testid="gov-f-jur" className="bg-white/10 border-white/15 text-white placeholder:text-blue-200/50" />
          <Input placeholder="Solicitation #" value={form.solicitation_no} onChange={(e) => setForm({ ...form, solicitation_no: e.target.value })} className="bg-white/10 border-white/15 text-white placeholder:text-blue-200/50" />
          <Input type="date" placeholder="Deadline" value={form.deadline} onChange={(e) => setForm({ ...form, deadline: e.target.value })} className="bg-white/10 border-white/15 text-white placeholder:text-blue-200/50" />
          <Input placeholder="Source URL" value={form.source_url} onChange={(e) => setForm({ ...form, source_url: e.target.value })} className="bg-white/10 border-white/15 text-white placeholder:text-blue-200/50" />
          <Input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="sm:col-span-2 bg-white/10 border-white/15 text-white placeholder:text-blue-200/50" />
          <Button type="submit" data-testid="gov-add-submit" className="bg-teal-500 hover:bg-teal-600 sm:col-span-2">Save opportunity</Button>
        </form>
      )}

      <div className="space-y-3">
        {!loaded && <div className="py-10 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-teal-400" /></div>}
        {loaded && opps.length === 0 && <p className="text-blue-200/60">No opportunities yet.</p>}
        {opps.map((o) => (
          <motion.div key={o.id} initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }} className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid={`gov-opp-${o.id}`}>
            <div className="flex items-start justify-between flex-wrap gap-2">
              <div>
                <h3 className="font-display font-bold text-lg">{o.title}</h3>
                <p className="text-sm text-blue-200/70">{o.agency} · {o.jurisdiction}{o.solicitation_no ? ` · ${o.solicitation_no}` : ""}</p>
              </div>
              <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full ${STAGE_TONE[o.stage] || "bg-white/10 text-blue-200"}`} data-testid={`gov-opp-stage-${o.id}`}>{STAGE_LABEL[o.stage] || o.stage}</span>
            </div>
            <div className="mt-2 flex flex-wrap gap-4 text-xs text-blue-200/70">
              {o.deadline && <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> Deadline {o.deadline}</span>}
              {o.source_url && <a href={o.source_url} target="_blank" rel="noreferrer" className="text-teal-300 hover:underline inline-flex items-center gap-1">Source <ExternalLink className="w-3 h-3" /></a>}
            </div>
            {o.description && <p className="text-sm text-blue-100/90 mt-2">{o.description}</p>}

            <div className="mt-4 flex flex-wrap items-center gap-2">
              {isMgmt && <Button size="sm" onClick={() => analyze(o.id)} disabled={busy === o.id + "-a"} data-testid={`gov-analyze-${o.id}`} className="bg-teal-500 hover:bg-teal-600 gap-1.5">{busy === o.id + "-a" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Analyze</Button>}
              <select value={o.stage} onChange={(e) => setStage(o.id, e.target.value)} data-testid={`gov-stage-select-${o.id}`} className="h-9 rounded-lg bg-white/10 border border-white/15 text-white px-3 text-sm">
                {stages.filter((s) => s !== "submitted").map((s) => <option key={s} value={s} className="text-slate-900">{STAGE_LABEL[s] || s}</option>)}
              </select>
              {isMgmt && <Button size="sm" variant="outline" onClick={() => prepare(o.id)} disabled={busy === o.id + "-p"} data-testid={`gov-prepare-${o.id}`} className="border-white/20 text-white bg-white/5 hover:bg-white/10 gap-1.5"><ShieldCheck className="w-4 h-4 text-teal-400" /> Prepare submission</Button>}
              {isMgmt && <Button size="sm" variant="outline" onClick={() => submit(o.id)} disabled={busy === o.id + "-s"} data-testid={`gov-submit-${o.id}`} className="border-white/20 text-white bg-white/5 hover:bg-white/10 gap-1.5"><Send className="w-4 h-4 text-teal-400" /> Mark submitted</Button>}
              {(o.analysis || o.checklist?.length > 0) && <button onClick={() => setExpanded(expanded === o.id ? null : o.id)} data-testid={`gov-toggle-${o.id}`} className="text-xs text-teal-300 hover:underline">{expanded === o.id ? "Hide" : "View"} analysis</button>}
            </div>

            {expanded === o.id && (
              <div className="mt-4 border-t border-white/10 pt-4" data-testid={`gov-analysis-${o.id}`}>
                {o.analysis && <div className="bg-blue-900/40 border border-teal-400/20 rounded-xl p-4 text-sm text-blue-50 whitespace-pre-wrap leading-relaxed">{o.analysis}</div>}
                {o.checklist?.length > 0 && (
                  <div className="mt-3">
                    <p className="text-xs uppercase tracking-wide text-teal-300 font-semibold mb-2">Required-documents checklist</p>
                    <div className="grid sm:grid-cols-2 gap-1.5">{o.checklist.map((c) => <span key={c} className="text-sm text-blue-100 inline-flex items-center gap-2"><CheckCircle2 className="w-3.5 h-3.5 text-teal-400 shrink-0" /> {c}</span>)}</div>
                  </div>
                )}
              </div>
            )}
          </motion.div>
        ))}
      </div>
      <p className="text-xs text-amber-300/80 mt-5 flex items-start gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> Submissions require an approved internal review in the Approval Center. AI never submits to a government body automatically.</p>
    </div>
  );
}

const PROFILE_LABELS = {
  legal_name: "Legal Company Name", uei: "UEI", cage: "CAGE Code", naics: "NAICS Codes",
  sam_status: "SAM.gov Status", certifications: "Certifications", licenses: "Licenses",
  service_areas: "Service Areas", insurance: "Insurance", capabilities: "Capabilities", past_performance: "Past Performance",
};

function ProfilePanel({ isAdmin }) {
  const [profile, setProfile] = useState(null);
  const [note, setNote] = useState("");
  const [edit, setEdit] = useState({});
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/gov/profile").then((r) => { setProfile(r.data.profile); setNote(r.data.note); }).catch(() => {});
  useEffect(() => { load(); }, []);
  if (!profile) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-teal-400" /></div>;

  const save = async () => {
    const payload = Object.fromEntries(Object.entries(edit).filter(([, v]) => v !== undefined && v !== ""));
    if (Object.keys(payload).length === 0) { toast.info("Nothing to save"); return; }
    setSaving(true);
    try { await api.post("/gov/profile", payload); toast.success("Capability profile updated"); setEdit({}); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); } finally { setSaving(false); }
  };

  return (
    <div data-testid="ent-profile">
      <p className="text-sm text-amber-300/90 mb-4 flex items-start gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {note}</p>
      <div className="rounded-2xl border border-white/10 overflow-hidden">
        {Object.entries(PROFILE_LABELS).map(([key, label], i) => {
          const val = profile[key];
          const unfilled = val === "NOT YET PROVIDED";
          return (
            <div key={key} className={`flex items-center justify-between gap-3 px-5 py-3.5 ${i % 2 ? "bg-white/[0.03]" : "bg-transparent"}`} data-testid={`gov-profile-${key}`}>
              <span className="text-sm text-blue-200/80 font-medium w-40 shrink-0">{label}</span>
              {isAdmin ? (
                <Input defaultValue={unfilled ? "" : val} placeholder="NOT YET PROVIDED" onChange={(e) => setEdit({ ...edit, [key]: e.target.value })} data-testid={`gov-profile-input-${key}`} className="flex-1 bg-white/10 border-white/15 text-white placeholder:text-amber-300/50" />
              ) : (
                <span className={`text-sm text-right ${unfilled ? "text-amber-300 bg-amber-500/10 px-2.5 py-1 rounded-full text-xs font-semibold" : "text-blue-50"}`}>{val}</span>
              )}
            </div>
          );
        })}
      </div>
      {isAdmin && <Button onClick={save} disabled={saving} data-testid="gov-profile-save" className="mt-4 bg-teal-500 hover:bg-teal-600 gap-1.5">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />} Save profile</Button>}
    </div>
  );
}

function ProgramsPanel({ isMgmt }) {
  const [programs, setPrograms] = useState([]);
  const [form, setForm] = useState({ name: "", sponsor: "", capacity: 0, description: "" });
  const load = () => api.get("/gov/programs").then((r) => setPrograms(r.data.programs)).catch(() => {});
  useEffect(() => { load(); }, []);

  const add = async (e) => {
    e.preventDefault();
    try { await api.post("/gov/programs", { ...form, capacity: Number(form.capacity) || 0 }); toast.success("Program created"); setForm({ name: "", sponsor: "", capacity: 0, description: "" }); load(); }
    catch (er) { toast.error(er?.response?.data?.detail || "Could not create program"); }
  };

  return (
    <div data-testid="ent-programs">
      <p className="text-sm text-blue-200/70 mb-4">Manage public-sector programs and provider networks — capacity, participants and sponsoring agency.</p>
      {isMgmt && (
        <form onSubmit={add} className="bg-white/5 border border-white/10 rounded-2xl p-5 mb-5 grid sm:grid-cols-2 gap-3" data-testid="prog-add-form">
          <Input required placeholder="Program name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="prog-f-name" className="bg-white/10 border-white/15 text-white placeholder:text-blue-200/50" />
          <Input placeholder="Sponsor / agency" value={form.sponsor} onChange={(e) => setForm({ ...form, sponsor: e.target.value })} data-testid="prog-f-sponsor" className="bg-white/10 border-white/15 text-white placeholder:text-blue-200/50" />
          <Input type="number" placeholder="Capacity" value={form.capacity} onChange={(e) => setForm({ ...form, capacity: e.target.value })} className="bg-white/10 border-white/15 text-white placeholder:text-blue-200/50" />
          <Input placeholder="Description" value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} className="bg-white/10 border-white/15 text-white placeholder:text-blue-200/50" />
          <Button type="submit" data-testid="prog-add-submit" className="bg-teal-500 hover:bg-teal-600 sm:col-span-2 gap-1.5"><Plus className="w-4 h-4" /> Create program</Button>
        </form>
      )}
      <div className="grid sm:grid-cols-2 gap-3">
        {programs.length === 0 && <p className="text-blue-200/60">No programs yet.</p>}
        {programs.map((p) => (
          <div key={p.id} className="bg-white/5 border border-white/10 rounded-2xl p-5" data-testid={`prog-${p.id}`}>
            <div className="flex items-center justify-between"><Layers className="w-5 h-5 text-teal-300" /><span className="text-[10px] uppercase font-semibold px-2 py-0.5 rounded-full bg-teal-500/15 text-teal-300">{p.status}</span></div>
            <h3 className="font-display font-bold text-lg mt-2">{p.name}</h3>
            {p.sponsor && <p className="text-sm text-blue-200/70">{p.sponsor}</p>}
            {p.description && <p className="text-sm text-blue-100/80 mt-1">{p.description}</p>}
            <div className="mt-3 grid grid-cols-3 gap-2 text-center">
              <div className="bg-white/5 rounded-xl p-2"><p className="font-display text-xl font-bold">{p.capacity || 0}</p><p className="text-[10px] text-blue-200/60">Capacity</p></div>
              <div className="bg-white/5 rounded-xl p-2"><p className="font-display text-xl font-bold">{p.participants || 0}</p><p className="text-[10px] text-blue-200/60">Participants</p></div>
              <div className="bg-white/5 rounded-xl p-2"><p className="font-display text-xl font-bold">{p.providers || 0}</p><p className="text-[10px] text-blue-200/60">Providers</p></div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function SecurityPanel() {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/security/posture").then((r) => setD(r.data)).catch(() => toast.error("Could not load security posture")); }, []);
  if (!d) return <div className="py-16 grid place-items-center"><Loader2 className="w-6 h-6 animate-spin text-teal-400" /></div>;
  return (
    <div data-testid="ent-security">
      <div className="flex items-center gap-3 mb-4">
        <div className="bg-white/5 border border-white/10 rounded-2xl px-5 py-3"><p className="font-display text-2xl font-bold" data-testid="sec-audit-events">{d.audit_events}</p><p className="text-xs text-blue-200/70">Audit events recorded</p></div>
      </div>
      <div className="space-y-2">
        {d.controls.map((c) => (
          <div key={c.control} className="bg-white/5 border border-white/10 rounded-2xl p-4 flex items-start justify-between gap-3" data-testid={`sec-control-${c.control.replace(/\s+/g, "-").toLowerCase()}`}>
            <div className="flex items-start gap-3">
              {c.status === "active" ? <CheckCircle2 className="w-5 h-5 text-teal-400 shrink-0 mt-0.5" /> : c.status === "partial" ? <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0 mt-0.5" /> : <ShieldAlert className="w-5 h-5 text-slate-400 shrink-0 mt-0.5" />}
              <div><p className="font-semibold text-sm">{c.control}</p><p className="text-xs text-blue-200/70 mt-0.5">{c.detail}</p></div>
            </div>
            <span className={`text-[10px] uppercase font-bold px-2 py-0.5 rounded-full shrink-0 ${SEC_TONE[c.status]}`}>{c.status.replace(/_/g, " ")}</span>
          </div>
        ))}
      </div>
      <p className="text-xs text-amber-300/80 mt-5 flex items-start gap-2"><AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" /> {d.note}</p>
    </div>
  );
}
